import API from 'files/api.js';

/* ==========================================================================
   Movement through the site.

   Self-hosted Umami, which counts without identifying anybody: no cookie, no
   localStorage, no device identifier of any kind. Uniqueness comes from a hash
   of the address and user agent against a salt that rotates every day and is
   then thrown away, so today's visitors cannot be matched to yesterday's or
   worked back to a person. That is what allows this to run without a consent
   banner, and the reasoning is written out in discoverability.md.

   Two decisions worth knowing about before editing anything here.

   Automatic tracking is off. Umami's script can watch the history api and fire
   a view whenever the url changes, which works, but it means the analytics
   decides what counts as a page rather than the app deciding. This site pushes
   history from one place in App.js, and that is the honest definition of a
   navigation here, so the view is sent from there. It also means the Quick Look
   opening over an article does not silently register as leaving the article.

   Nothing here fails loudly. An unreachable server, a blocked script, a browser
   that refuses it: every one of those ends with the site working exactly as it
   does now. Measurement is never allowed to be a dependency of the thing being
   measured.
   ========================================================================== */

/* WEBSITE is the only thing that turns this on. Empty and every function here
   returns without doing anything, which is how it shipped ahead of the install.

   HOST is deliberately empty, meaning same origin.

   Apache serves the tracker from this domain, so a relative path is correct in
   production and has the useful side effect of being correct locally too: the
   development server proxies unknown paths to duralabs.io, so /pulse.js and the
   endpoint it posts to both resolve without ever becoming a cross-origin
   request. Set HOST only if the tracker ever moves to another hostname, and
   accept that doing so brings CORS with it.

   SCRIPT is renamed from Umami's default of script.js. That exact path is on
   every blocklist there is; a first-party file with an unremarkable name is
   not. See discoverability.md for the proxy setup. */
const HOST = '';
const SCRIPT = '/pulse.js';
const WEBSITE = '08719320-fc56-47fd-9922-95c756f1ac40';

/* The development site.

   Which of the two a build reports to is API.dev_analytics, so switching is a
   flag rather than something to remember to set and remember to unset. `true`
   also lifts the localhost guard below, because a development build that tracks
   nothing is not much of a test.

   Two sites rather than one, deliberately. An afternoon of clicking through a
   development build is not traffic, and once it is mixed into the real figures
   there is no separating it out again. */
const DEVELOPMENT = '0f2dca94-f9c1-4d71-b9d5-2116e5adb76f';

// the site events are attributed to
const site = () => (API.dev_analytics ? DEVELOPMENT : WEBSITE);

/* Queued until the tracker lands, because the first view is the one worth most.

   The script is deferred, so a navigation can happen before it exists. Dropping
   those would lose the entry page, which is the single most useful thing this
   collects: it is the answer to which article is actually bringing people in. */
let pending = [];
let ready = false;

/* Signals that say "do not measure me". Neither is legally required here, since
   nothing is stored on the device and no consent is owed. They are honoured
   because somebody who has set them has said something plain, and a firm whose
   entire argument is about care should not need a law to listen. */
const refused = () => {
    if(typeof window === 'undefined' || !window.navigator) {
        return true;
    }
    return window.navigator.doNotTrack === '1'
        || window.doNotTrack === '1'
        || window.navigator.msDoNotTrack === '1'
        || window.navigator.globalPrivacyControl === true;
};

/* Local work is not traffic. Without this, a day spent building the site
   produces a chart that says the site had a very good day. */
const local = () => {
    const host = window.location.hostname;
    return host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local') || host.startsWith('192.168.');
};

const configured = () => {

    // never, under any circumstances, for somebody who has asked not to be
    if(refused()) {
        return false;
    }
    if(!site()) {
        return false;
    }

    /* The development flag is the one case where the localhost guard is meant
       to be lifted, because it already sends somewhere else. */
    return API.dev_analytics || !local();
};

/* Sends one view. Called after the url has already been pushed, so the tracker
   reads the right address from the page rather than being told it twice.

   `from` is passed explicitly because document.referrer never changes inside a
   single page app: it holds whatever site sent the visitor here, for the whole
   visit. Without this every internal step would look like a direct arrival and
   there would be no path to reconstruct. */
const send = view => {

    const umami = window.umami;
    if(!umami) {
        return false;
    }

    // the queue carries both kinds, because an event fired before the tracker
    // lands is exactly as worth keeping as a view
    if(view.event) {
        try {
            umami.track(view.event, view.data || {});
            return true;
        } catch(e) {
            return false;
        }
    }

    /* The payload override form, which not every tracker version carries. When
       it is missing, a plain call still records the view correctly, because the
       url is already right; only the internal referrer is lost. Degrading to
       slightly less detail beats throwing inside an analytics call. */
    try {
        if(view.from) {
            umami.track(props => ({ ...props, referrer: view.from, url: view.path }));
            return true;
        }
        umami.track();
        return true;

    } catch(e) {
        try {
            umami.track();
            return true;
        } catch(ignored) {
            return false;
        }
    }
};

const flush = () => {
    ready = true;
    const queued = pending;
    pending = [];
    queued.forEach(send);
};

/* ==========================================================================
   Where a visit came from.

   Held for the tab and nothing longer. sessionStorage, not a cookie and not
   localStorage, which is the same promise the rest of this file makes: nothing
   that survives the visit, nothing that can be matched to a person, nothing that
   owes anybody a banner.

   The cost of that is worth stating plainly rather than discovering later. A
   visitor who clicks an ad today, thinks about it, and comes back on Thursday to
   fill in the form arrives on Thursday with no campaign attached, and that lead
   will read as direct. Cross-visit attribution is exactly the thing a cookie
   buys, and it is the thing we have decided not to buy. Same-visit conversions
   are attributed correctly and are most of them; the rest are undercounted in a
   direction we know about, which beats being wrong in a direction we do not.

   Last touch, not first. If somebody arrives on one ad and returns on another
   inside the same visit, the second one is what sent them the time that counted.
   An arrival carrying no parameters never overwrites one that does, so moving
   around the site cannot erase how the visit began.
   ========================================================================== */

const CAMPAIGN = 'dura.campaign';

/* The click identifiers matter as much as the utm tags. Each platform mints its
   own and will only credit a conversion reported back under it: gclid for Google,
   msclkid for Microsoft, rdt_cid for Reddit, fbclid for Meta, li_fat_id for
   LinkedIn. Capturing them is what lets a real conversation, weeks later, be
   reported against the click that started it without a tag on the page.

   Add to this list before running a campaign on a new platform, not after. The
   parameter only exists on the click itself — traffic that arrives while its
   identifier is unlisted cannot be attributed retrospectively, because the value
   was never anywhere but that URL. */
const PARAMS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
    'gclid', 'msclkid', 'rdt_cid', 'fbclid', 'li_fat_id'
];

const external = () => {
    try {
        if(!document.referrer) return '';
        const host = new URL(document.referrer).hostname;
        return host === window.location.hostname ? '' : host;
    } catch(e) {
        return '';
    }
};

/* Read once, at boot, before the app rewrites the address.

   App's history effect normalises the url to the canonical path for the active
   view as soon as it mounts, and that rewrite takes the query string with it. By
   the time anything else could ask, ?utm_source is gone. */
/* Run at import, deliberately, and not from an effect.

   App normalises the address in an effect declared well before the one that
   would call this, and effects run in the order they are declared — so by the
   time any component could ask, /?utm_source=... has already become /. Module
   scope executes during import, which is the last moment the parameters still
   exist. This is the same trap the debug probe fell into; it is worth knowing
   that anything on this page needing the query string has to read it here. */
export const captureCampaign = () => {

    try {
        const query = new URLSearchParams(window.location.search);
        const found = {};
        PARAMS.forEach(key => {
            const value = query.get(key);
            if(value) found[key] = value.slice(0, 120);
        });

        // an arrival with no campaign on it leaves whatever is already held
        if(Object.keys(found).length === 0) {
            return campaign();
        }

        found.landing = window.location.pathname;
        found.referrer = external();
        found.at = new Date().toISOString();

        window.sessionStorage.setItem(CAMPAIGN, JSON.stringify(found));
        return found;

    } catch(e) {
        return {};
    }
};

/* What is known about how this visit started. Always an object, never null, so
   callers can spread it without checking. */
export const campaign = () => {
    try {
        return JSON.parse(window.sessionStorage.getItem(CAMPAIGN) || '{}') || {};
    } catch(e) {
        return {};
    }
};

/* Run at import, deliberately, and not from an effect.

   App normalises the address in an effect declared well before any component
   could call this, and effects run in the order they are declared — so by the
   time anything else asks, /?utm_source=... has already become /. Module scope
   executes during import, which is the last moment the parameters still exist.
   Anything on this site that needs the query string has to read it here. */
captureCampaign();

/* One named thing that happened, with the campaign attached.

   Every event carries its acquisition context, so a funnel can be read per
   source without joining anything together afterwards: Umami can answer "how
   many Quick Looks were completed by people who arrived on this campaign"
   directly, which is the question ad spend actually turns on.

   Never called with anything identifying. Names, emails and phone numbers go to
   our own server on the form post and nowhere else — an analytics event says
   that a form was submitted, never by whom. */
export const trackEvent = (name, data) => {

    if(!configured() || !name) {
        return;
    }

    const payload = { ...campaign(), ...(data || {}) };

    try {
        const umami = window.umami;
        if(umami && typeof umami.track === 'function') {
            umami.track(name, payload);
            return;
        }
    } catch(e) {
        // an event that cannot be recorded is not an error worth surfacing
    }

    if(pending.length < 20) {
        pending.push({ event: name, data: payload });
    }
};

/* Loads the tracker once. Safe to call repeatedly; only the first call does
   anything. */
export const startAnalytics = () => {

    if(!configured() || document.getElementById('dl-pulse')) {
        return;
    }

    /* A flag left on and deployed would quietly file every real visit under the
       development site until somebody noticed the live one had gone flat. It is
       not worth blocking, since a staging host would want exactly this, but it
       is worth saying out loud where anybody opening the console would see it. */
    if(API.dev_analytics && !local()) {
        console.warn('Analytics: reporting to the development site from a live host. See dev_analytics in files/api.js.');
    }

    const script = document.createElement('script');
    script.async = true;
    script.id = 'dl-pulse';
    script.src = `${HOST}${SCRIPT}`;
    script.setAttribute('data-website-id', site());

    // the app decides what a navigation is, not the tracker — see the top of
    // this file
    script.setAttribute('data-auto-track', 'false');

    script.onload = flush;

    // a blocked or missing script is not a problem to report; it just means
    // this visit goes uncounted, and the queue is dropped so it cannot grow
    script.onerror = () => { pending = []; ready = false; };

    document.head.appendChild(script);
};

/* One navigation. `path` is where they now are, `from` is where they came from
   within the site, or null on the first view of a visit. */
export const trackView = (path, from) => {

    if(!configured()) {
        return;
    }

    /* Sent the moment a tracker exists, rather than the moment our own script
       reports that it loaded. Those are usually the same instant and
       occasionally are not: a cached script, a second mount, or anything else
       that puts the tracker on the page without firing our onload. Waiting on
       our own load event in those cases silently drops every view. */
    const view = { from: from || null, path: path };
    if((ready || window.umami) && send(view)) {
        return;
    }

    /* Bounded. If the tracker never arrives, this must not become a slow leak
       in a tab somebody leaves open for a week. */
    if(pending.length < 20) {
        pending.push(view);
    }
};
